/**
 * Visitor Management API service (ADR-073).
 * Endpoints mounted at /api/v2/visitor-management/
 *
 * Typed `@tiaki/http-client` surface per ADR 113 §Decision (path autocomplete
 * from the OpenAPI schema).
 *
 * Pattern:
 *   const { data, error } = await client.GET('/api/v2/...', {
 *     params: { query: { ... } },
 *   });
 *   if (error) throw error;
 *   return data;
 *
 * `as never` casts on params/body bridge the drf-spectacular schema's looser
 * shapes (notably FormData vs typed body payloads).
 */
import { client } from '../lib/http-client';
import type {
  VisitorListItem,
  VisitorDetail,
  VisitorCreate,
  VisitListItem,
  VisitDetail,
  VisitReasonType,
  VisitReasonTypeWrite,
  VisitorCategoryType,
  VisitorCategoryTypeWrite,
  WatchListEntry,
  WatchListEntryWrite,
  ScreeningResultItem,
  BadgeTemplate,
  BadgeTemplateWrite,
  ScreeningConfiguration,
  ScreeningConfigurationWrite,
  CheckInRequest,
  CheckInResponse,
  CheckOutRequest,
  VisitorFilters,
  VisitFilters,
  WatchListFilters,
  PaginatedResponse,
  EmployeeReasonType,
  EmployeeReasonTypeWrite,
  EmployeeVisitListItem,
  EmployeeVisitDetail,
  EmployeeVisitCreate,
  EmployeeVisitUpdate,
  CarRiderQueueEntry,
  CarRiderQueueEntryWrite,
  PickupDelegation,
  PickupDelegationWrite,
} from '../types/VisitorManagementTypes';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

/**
 * Stringify filters into the openapi-fetch `params.query` shape. The lib
 * accepts arrays / numbers / strings; the helper keeps SMS's existing
 * "drop empties" semantics so a `''`/`null`/`undefined` in a filter object
 * doesn't end up on the wire as `?key=`.
 */
function buildQuery(
  filters: Record<string, unknown>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      out[key] = value as string | number | boolean;
    }
  }
  return out;
}

// =============================================================================
// Visitors
// =============================================================================

export async function getVisitors(
  filters: VisitorFilters = {},
): Promise<PaginatedResponse<VisitorListItem>> {
  const result = await client.GET('/api/v2/visitor-management/visitors/', {
    params: { query: buildQuery(filters as Record<string, unknown>) as never },
  });
  return unwrap(result) as unknown as PaginatedResponse<VisitorListItem>;
}

export async function getVisitorById(publicId: string): Promise<VisitorDetail> {
  const result = await client.GET(
    '/api/v2/visitor-management/visitors/{public_id}/',
    { params: { path: { public_id: publicId } as never } },
  );
  return unwrap(result) as unknown as VisitorDetail;
}

export async function createVisitor(
  data: VisitorCreate,
): Promise<VisitorDetail> {
  const result = await client.POST('/api/v2/visitor-management/visitors/', {
    body: data as never,
  });
  return unwrap(result) as unknown as VisitorDetail;
}

export async function updateVisitor(
  publicId: string,
  data: Partial<VisitorCreate>,
): Promise<VisitorDetail> {
  const result = await client.PATCH(
    '/api/v2/visitor-management/visitors/{public_id}/',
    {
      params: { path: { public_id: publicId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as VisitorDetail;
}

export async function searchVisitors(
  query: string,
): Promise<VisitorListItem[]> {
  const result = await client.GET(
    '/api/v2/visitor-management/visitors/search/',
    { params: { query: { q: query } as never } },
  );
  return unwrap(result) as unknown as VisitorListItem[];
}

export async function getVisitorHistory(
  publicId: string,
): Promise<VisitListItem[]> {
  const result = await client.GET(
    '/api/v2/visitor-management/visitors/{public_id}/history/',
    { params: { path: { public_id: publicId } as never } },
  );
  return unwrap(result) as unknown as VisitListItem[];
}

// =============================================================================
// Visits
// =============================================================================

export async function getVisits(
  filters: VisitFilters = {},
): Promise<PaginatedResponse<VisitListItem>> {
  const result = await client.GET('/api/v2/visitor-management/visits/', {
    params: { query: buildQuery(filters as Record<string, unknown>) as never },
  });
  return unwrap(result) as unknown as PaginatedResponse<VisitListItem>;
}

export async function getVisitById(publicId: string): Promise<VisitDetail> {
  const result = await client.GET(
    '/api/v2/visitor-management/visits/{public_id}/',
    { params: { path: { public_id: publicId } as never } },
  );
  return unwrap(result) as unknown as VisitDetail;
}

export async function getActiveVisits(
  school?: number,
): Promise<VisitListItem[]> {
  const result = await client.GET('/api/v2/visitor-management/visits/active/', {
    params: {
      query: (school ? { school } : {}) as never,
    },
  });
  return unwrap(result) as unknown as VisitListItem[];
}

export async function getTodayVisits(
  school?: number,
): Promise<VisitListItem[]> {
  const result = await client.GET('/api/v2/visitor-management/visits/today/', {
    params: {
      query: (school ? { school } : {}) as never,
    },
  });
  return unwrap(result) as unknown as VisitListItem[];
}

// =============================================================================
// Check-In / Check-Out Actions
// =============================================================================

export async function checkIn(data: CheckInRequest): Promise<CheckInResponse> {
  const result = await client.POST('/api/v2/visitor-management/check-in/', {
    body: data as never,
  });
  return unwrap(result) as unknown as CheckInResponse;
}

export async function checkOut(data: CheckOutRequest): Promise<VisitDetail> {
  const result = await client.POST('/api/v2/visitor-management/check-out/', {
    body: data as never,
  });
  return unwrap(result) as unknown as VisitDetail;
}

// =============================================================================
// Watch List
// =============================================================================

export async function getWatchListEntries(
  filters: WatchListFilters = {},
): Promise<PaginatedResponse<WatchListEntry>> {
  const result = await client.GET('/api/v2/visitor-management/watch-list/', {
    params: { query: buildQuery(filters as Record<string, unknown>) as never },
  });
  return unwrap(result) as unknown as PaginatedResponse<WatchListEntry>;
}

export async function createWatchListEntry(
  data: WatchListEntryWrite | FormData,
): Promise<WatchListEntry> {
  const result = await client.POST('/api/v2/visitor-management/watch-list/', {
    body: data as never,
  });
  return unwrap(result) as unknown as WatchListEntry;
}

export async function updateWatchListEntry(
  publicId: string,
  data: Partial<WatchListEntryWrite> | FormData,
): Promise<WatchListEntry> {
  const result = await client.PATCH(
    '/api/v2/visitor-management/watch-list/{public_id}/',
    {
      params: { path: { public_id: publicId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as WatchListEntry;
}

export async function deleteWatchListEntry(publicId: string): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/visitor-management/watch-list/{public_id}/',
    { params: { path: { public_id: publicId } as never } },
  );
  const __resp = result as { error?: unknown };
  if (__resp.error !== undefined) throw __resp.error;
}

// =============================================================================
// Screening Results
// =============================================================================

export async function getScreeningResults(): Promise<
  PaginatedResponse<ScreeningResultItem>
> {
  const result = await client.GET(
    '/api/v2/visitor-management/screening-results/',
    {},
  );
  return unwrap(result) as unknown as PaginatedResponse<ScreeningResultItem>;
}

// =============================================================================
// Visit Reason Types
// =============================================================================

export async function getReasonTypes(): Promise<VisitReasonType[]> {
  const result = await client.GET(
    '/api/v2/visitor-management/reason-types/',
    {},
  );
  return unwrap(result) as unknown as VisitReasonType[];
}

export async function createReasonType(
  data: VisitReasonTypeWrite,
): Promise<VisitReasonType> {
  const result = await client.POST('/api/v2/visitor-management/reason-types/', {
    body: data as never,
  });
  return unwrap(result) as unknown as VisitReasonType;
}

export async function updateReasonType(
  publicId: string,
  data: Partial<VisitReasonTypeWrite>,
): Promise<VisitReasonType> {
  const result = await client.PATCH(
    '/api/v2/visitor-management/reason-types/{public_id}/',
    {
      params: { path: { public_id: publicId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as VisitReasonType;
}

export async function deleteReasonType(publicId: string): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/visitor-management/reason-types/{public_id}/',
    { params: { path: { public_id: publicId } as never } },
  );
  const __resp = result as { error?: unknown };
  if (__resp.error !== undefined) throw __resp.error;
}

export async function getReasonTypeById(id: string): Promise<VisitReasonType> {
  const result = await client.GET(
    '/api/v2/visitor-management/reason-types/{public_id}/',
    { params: { path: { public_id: id } as never } },
  );
  return unwrap(result) as unknown as VisitReasonType;
}

// =============================================================================
// Visitor Category Types
// =============================================================================

export async function getCategoryTypes(): Promise<VisitorCategoryType[]> {
  const result = await client.GET(
    '/api/v2/visitor-management/category-types/',
    {},
  );
  return unwrap(result) as unknown as VisitorCategoryType[];
}

export async function createCategoryType(
  data: VisitorCategoryTypeWrite,
): Promise<VisitorCategoryType> {
  const result = await client.POST(
    '/api/v2/visitor-management/category-types/',
    { body: data as never },
  );
  return unwrap(result) as unknown as VisitorCategoryType;
}

export async function updateCategoryType(
  publicId: string,
  data: Partial<VisitorCategoryTypeWrite>,
): Promise<VisitorCategoryType> {
  const result = await client.PATCH(
    '/api/v2/visitor-management/category-types/{public_id}/',
    {
      params: { path: { public_id: publicId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as VisitorCategoryType;
}

export async function deleteCategoryType(publicId: string): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/visitor-management/category-types/{public_id}/',
    { params: { path: { public_id: publicId } as never } },
  );
  const __resp = result as { error?: unknown };
  if (__resp.error !== undefined) throw __resp.error;
}

export async function getCategoryTypeById(
  id: string,
): Promise<VisitorCategoryType> {
  const result = await client.GET(
    '/api/v2/visitor-management/category-types/{public_id}/',
    { params: { path: { public_id: id } as never } },
  );
  return unwrap(result) as unknown as VisitorCategoryType;
}

// =============================================================================
// Badge Templates
// =============================================================================

export async function getBadgeTemplates(): Promise<BadgeTemplate[]> {
  const result = await client.GET(
    '/api/v2/visitor-management/badge-templates/',
    {},
  );
  return unwrap(result) as unknown as BadgeTemplate[];
}

export async function createBadgeTemplate(
  data: BadgeTemplateWrite,
): Promise<BadgeTemplate> {
  const result = await client.POST(
    '/api/v2/visitor-management/badge-templates/',
    { body: data as never },
  );
  return unwrap(result) as unknown as BadgeTemplate;
}

export async function updateBadgeTemplate(
  publicId: string,
  data: Partial<BadgeTemplateWrite>,
): Promise<BadgeTemplate> {
  const result = await client.PATCH(
    '/api/v2/visitor-management/badge-templates/{public_id}/',
    {
      params: { path: { public_id: publicId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as BadgeTemplate;
}

export async function deleteBadgeTemplate(publicId: string): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/visitor-management/badge-templates/{public_id}/',
    { params: { path: { public_id: publicId } as never } },
  );
  const __resp = result as { error?: unknown };
  if (__resp.error !== undefined) throw __resp.error;
}

export async function getBadgeTemplateById(id: string): Promise<BadgeTemplate> {
  const result = await client.GET(
    '/api/v2/visitor-management/badge-templates/{public_id}/',
    { params: { path: { public_id: id } as never } },
  );
  return unwrap(result) as unknown as BadgeTemplate;
}

// =============================================================================
// Screening Configuration
// =============================================================================

export async function getScreeningConfig(): Promise<ScreeningConfiguration[]> {
  const result = await client.GET(
    '/api/v2/visitor-management/screening-config/',
    {},
  );
  return unwrap(result) as unknown as ScreeningConfiguration[];
}

export async function updateScreeningConfig(
  publicId: string,
  data: ScreeningConfigurationWrite,
): Promise<ScreeningConfiguration> {
  const result = await client.PATCH(
    '/api/v2/visitor-management/screening-config/{public_id}/',
    {
      params: { path: { public_id: publicId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as ScreeningConfiguration;
}

export async function createScreeningConfig(
  data: ScreeningConfigurationWrite,
): Promise<ScreeningConfiguration> {
  const result = await client.POST(
    '/api/v2/visitor-management/screening-config/',
    { body: data as never },
  );
  return unwrap(result) as unknown as ScreeningConfiguration;
}

// =============================================================================
// Employee Reason Types (ADR-073 Phase 2)
// =============================================================================

export async function getEmployeeReasonTypes(): Promise<EmployeeReasonType[]> {
  const result = await client.GET(
    '/api/v2/visitor-management/employee-reason-types/',
    {},
  );
  return unwrap(result) as unknown as EmployeeReasonType[];
}

export async function getEmployeeReasonTypeById(
  id: string,
): Promise<EmployeeReasonType> {
  const result = await client.GET(
    '/api/v2/visitor-management/employee-reason-types/{public_id}/',
    { params: { path: { public_id: id } as never } },
  );
  return unwrap(result) as unknown as EmployeeReasonType;
}

export async function createEmployeeReasonType(
  data: EmployeeReasonTypeWrite,
): Promise<EmployeeReasonType> {
  const result = await client.POST(
    '/api/v2/visitor-management/employee-reason-types/',
    { body: data as never },
  );
  return unwrap(result) as unknown as EmployeeReasonType;
}

export async function updateEmployeeReasonType(
  publicId: string,
  data: Partial<EmployeeReasonTypeWrite>,
): Promise<EmployeeReasonType> {
  const result = await client.PATCH(
    '/api/v2/visitor-management/employee-reason-types/{public_id}/',
    {
      params: { path: { public_id: publicId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as EmployeeReasonType;
}

export async function deleteEmployeeReasonType(
  publicId: string,
): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/visitor-management/employee-reason-types/{public_id}/',
    { params: { path: { public_id: publicId } as never } },
  );
  const __resp = result as { error?: unknown };
  if (__resp.error !== undefined) throw __resp.error;
}

// =============================================================================
// Employee Visits (ADR-073 Phase 2)
// =============================================================================

export async function getEmployeeVisits(
  filters: Record<string, unknown> = {},
): Promise<PaginatedResponse<EmployeeVisitListItem>> {
  const result = await client.GET(
    '/api/v2/visitor-management/employee-visits/',
    {
      params: { query: buildQuery(filters) as never },
    },
  );
  return unwrap(result) as unknown as PaginatedResponse<EmployeeVisitListItem>;
}

export async function getActiveEmployeeVisits(
  schoolId?: number,
): Promise<EmployeeVisitListItem[]> {
  const query: Record<string, unknown> = {};
  if (schoolId) query.school = schoolId;
  const result = await client.GET(
    '/api/v2/visitor-management/employee-visits/active/',
    {
      params: { query: query as never },
    },
  );
  return unwrap(result) as unknown as EmployeeVisitListItem[];
}

export async function getTodayEmployeeVisits(
  schoolId?: number,
): Promise<EmployeeVisitListItem[]> {
  const query: Record<string, unknown> = {};
  if (schoolId) query.school = schoolId;
  const result = await client.GET(
    '/api/v2/visitor-management/employee-visits/today/',
    {
      params: { query: query as never },
    },
  );
  return unwrap(result) as unknown as EmployeeVisitListItem[];
}

export async function getEmployeeVisitById(
  id: string,
): Promise<EmployeeVisitDetail> {
  const result = await client.GET(
    '/api/v2/visitor-management/employee-visits/{public_id}/',
    { params: { path: { public_id: id } as never } },
  );
  return unwrap(result) as unknown as EmployeeVisitDetail;
}

export async function createEmployeeVisit(
  data: EmployeeVisitCreate,
): Promise<EmployeeVisitDetail> {
  const result = await client.POST(
    '/api/v2/visitor-management/employee-visits/',
    { body: data as never },
  );
  return unwrap(result) as unknown as EmployeeVisitDetail;
}

export async function updateEmployeeVisit(
  publicId: string,
  data: Partial<EmployeeVisitUpdate>,
): Promise<EmployeeVisitDetail> {
  const result = await client.PATCH(
    '/api/v2/visitor-management/employee-visits/{public_id}/',
    {
      params: { path: { public_id: publicId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as EmployeeVisitDetail;
}

export async function deleteEmployeeVisit(publicId: string): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/visitor-management/employee-visits/{public_id}/',
    { params: { path: { public_id: publicId } as never } },
  );
  const __resp = result as { error?: unknown };
  if (__resp.error !== undefined) throw __resp.error;
}

// =============================================================================
// Car Rider Queue (ADR-073 Phase 2)
// =============================================================================

export async function getCarRiderQueue(): Promise<CarRiderQueueEntry[]> {
  const result = await client.GET(
    '/api/v2/visitor-management/car-rider-queue/',
    {},
  );
  return unwrap(result) as unknown as CarRiderQueueEntry[];
}

export async function getCarRiderQueueEntryById(
  id: string,
): Promise<CarRiderQueueEntry> {
  const result = await client.GET(
    '/api/v2/visitor-management/car-rider-queue/{public_id}/',
    { params: { path: { public_id: id } } },
  );
  return unwrap(result) as unknown as CarRiderQueueEntry;
}

export async function createCarRiderQueueEntry(
  data: CarRiderQueueEntryWrite,
): Promise<CarRiderQueueEntry> {
  const result = await client.POST(
    '/api/v2/visitor-management/car-rider-queue/',
    { body: data },
  );
  return unwrap(result) as unknown as CarRiderQueueEntry;
}

export async function updateCarRiderQueueEntry(
  publicId: string,
  data: Partial<CarRiderQueueEntryWrite>,
): Promise<CarRiderQueueEntry> {
  const result = await client.PATCH(
    '/api/v2/visitor-management/car-rider-queue/{public_id}/',
    {
      params: { path: { public_id: publicId } },
      body: data,
    },
  );
  return unwrap(result) as unknown as CarRiderQueueEntry;
}

export async function deleteCarRiderQueueEntry(
  publicId: string,
): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/visitor-management/car-rider-queue/{public_id}/',
    { params: { path: { public_id: publicId } } },
  );
  const __resp = result as { error?: unknown };
  if (__resp.error !== undefined) throw __resp.error;
}

// =============================================================================
// Pickup Delegations (ADR-073 Phase 2)
// =============================================================================

export async function getPickupDelegations(): Promise<PickupDelegation[]> {
  const result = await client.GET(
    '/api/v2/visitor-management/pickup-delegations/',
    {},
  );
  return unwrap(result) as unknown as PickupDelegation[];
}

export async function getPickupDelegationById(
  id: string,
): Promise<PickupDelegation> {
  const result = await client.GET(
    '/api/v2/visitor-management/pickup-delegations/{public_id}/',
    { params: { path: { public_id: id } } },
  );
  return unwrap(result) as unknown as PickupDelegation;
}

export async function createPickupDelegation(
  data: PickupDelegationWrite,
): Promise<PickupDelegation> {
  const result = await client.POST(
    '/api/v2/visitor-management/pickup-delegations/',
    { body: data },
  );
  return unwrap(result) as unknown as PickupDelegation;
}

export async function updatePickupDelegation(
  publicId: string,
  data: Partial<PickupDelegationWrite>,
): Promise<PickupDelegation> {
  const result = await client.PATCH(
    '/api/v2/visitor-management/pickup-delegations/{public_id}/',
    {
      params: { path: { public_id: publicId } },
      body: data,
    },
  );
  return unwrap(result) as unknown as PickupDelegation;
}

export async function deletePickupDelegation(publicId: string): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/visitor-management/pickup-delegations/{public_id}/',
    { params: { path: { public_id: publicId } } },
  );
  const __resp = result as { error?: unknown };
  if (__resp.error !== undefined) throw __resp.error;
}
